import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Popover } from 'antd';
import { toast } from 'react-toastify';
import PlanToast from '../PlanToast/PlanToast';
import { truncateText } from '../../utils';
import PlayArrow from '@mui/icons-material/PlayArrow';
import EqualizerIcon from '@mui/icons-material/Equalizer';

interface VirtualizedSongRowProps {
  index: number;
  song: any;
  selectedSongs: any[];
  bulkAction: boolean;
  setBulkAction?: (value: boolean) => void;
  currentPage: number;
  selectSongFunction: (song: any, index: number) => void;
  selectSingleSong: (song: any, index: number) => void;
  rangeSongSelect?: (song: any, index: number) => void;
  isSelected: (song: any, selectedSongs: any[], currentPage: number, index: number) => boolean;
  isDragging?: boolean;
  playlistState?: any;
  menu?: any;
  hideMenu?: boolean;
  fromBrowse?: boolean;
  HideDates?: boolean;
  playlistDispatch?: any;
  SearchId?: string;
  onMenuOpen?: () => void;
  disableDrag?: boolean;
}

const VirtualizedSongRow = memo<VirtualizedSongRowProps>(({
  index,
  song,
  selectedSongs,
  bulkAction,
  setBulkAction,
  currentPage,
  selectSongFunction,
  selectSingleSong,
  rangeSongSelect,
  isSelected,
  playlistState,
  menu,
  hideMenu,
  fromBrowse,
  HideDates,
  playlistDispatch,
  SearchId,
  onMenuOpen,
  disableDrag = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: song.uniqueId || song.id || song._id,
    disabled: disableDrag,
  });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  const handleRowClick = (e: React.MouseEvent) => {

    if (isDragging) {
      return;
    }

    if (!bulkAction && setBulkAction) {
      setBulkAction(true);
    }

    // Use native event properties (more reliable than localStorage)
    // Also check localStorage as fallback for edge cases
    const ctrlHold = e.ctrlKey || e.metaKey || localStorage.getItem('ctrlHold') === 'true';
    const shiftHold = e.shiftKey || localStorage.getItem('shiftHold') === 'true';

    if (ctrlHold) {
      selectSongFunction(song, index);
    } else if (shiftHold && rangeSongSelect) {
      rangeSongSelect(song, index);
    } else {
      selectSingleSong(song, index);
    }
  };

  return (
    <table 
      ref={setNodeRef}
      style={{
        ...dragStyle,
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
      }}
      className="tablePlayList"
    >
      <tbody>
        <tr
          className={isSelected(song, selectedSongs, currentPage, index) ? 'selected rowTable' : 'rowTable'}
          onClick={handleRowClick}
        >
          {/* Song Number Column */}
          <td style={{ width: '63px', textAlign: 'center' }}>
            {fromBrowse ? (
              index + 1
            ) : (
              <>
                {playlistState?.previewSong?.id === song?.id ? (
                  <div
                    style={{ padding: '6px 0px 0px 0px ' }}
                    onClick={() => {
                      playlistDispatch?.({
                        type: 'SET_PREVIEW_SONG',
                        payload: {
                          url: '',
                          id: '',
                          name: '',
                          artist: '',
                          image: '',
                          external: '',
                        },
                      });
                    }}
                  >
                    <EqualizerIcon
                      style={{
                        marginLeft: '15px',
                      }}
                      className="icons45"
                    />
                  </div>
                ) : song?.key ? (
                  song?.key
                ) : (
                  <div
                    className="playButtonPlayList"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      if (song.previewUrl) {
                        if (song.clickHandleFn) {
                          song.clickHandleFn();
                        } else {
                          song?.clickHandle(
                            song?.previewUrl,
                            song?.id,
                            song?.image,
                            song?.title,
                            song?.artist,
                            song?.externalUrl
                          );
                        }
                      } else {
                      }
                    }}
                  >
                    {song.previewUrl ? (
                      <PlayArrow
                        style={{
                          color: 'white',
                        }}
                        className="icons1"
                      />
                    ) : (
                      <img
                        alt="spotifylogo"
                        src={'./../static/images/spotifyLogo.png'}
                        width={28}
                        height={28}
                        className="icons1"
                        onClick={() => {
                          window.open(song.externalUrl, '_blank');
                        }}
                      />
                    )}
                    <span
                      className="icons2"
                      style={{
                        width: '',
                      }}
                    >
                      {index + 1}
                    </span>
                  </div>
                )}
              </>
            )}
          </td>

          {/* Title & Artist Column with Drag Handle */}
          <td className="handle">
            <div
              {...(disableDrag ? {} : attributes)}
              {...(disableDrag ? {} : listeners)}
              style={{ cursor: disableDrag ? 'not-allowed' : 'grab', height: '100%', display: 'flex', alignItems: 'center' }}
              onMouseDown={disableDrag ? () => toast.warning('Please unselect songs before dragging. You cannot drag non-consecutive multiple songs.') : undefined}
            >
              {typeof song?.image === 'string' ? (
              <div className="imageName">
                <span className="imgSpan">
                  <span id="hancker">
                    <img
                      className="table-list-image"
                      alt="thumbnail"
                      src={song?.image}
                      onClick={() => {
                        song?.clickHandle(
                          song?.previewUrl,
                          song?.id,
                          song?.image,
                          song?.title,
                          song?.artist,
                          song?.externalUrl
                        );
                      }}
                      width="40"
                      height="40"
                    />
                  </span>
                </span>
                <div className="namePlaylist">
                  <>
                    {typeof song.title === 'string' ? (
                      <Popover
                        content={<p>{song?.title}</p>}
                        placement="topLeft"
                      >
                        <p
                          id="hancker"
                          onClick={() => {
                            song?.clickHandle(
                              song?.previewUrl,
                              song?.id,
                              song?.image,
                              song?.title,
                              song?.artist,
                              song?.externalUrl
                            );
                          }}
                        >
                          {truncateText(song?.title, 23)}
                        </p>
                      </Popover>
                    ) : (
                      <p
                        id="hancker"
                        onClick={() => {
                          song?.clickHandle(
                            song?.previewUrl,
                            song?.id,
                            song?.image,
                            song?.title,
                            song?.artist,
                            song?.externalUrl
                          );
                        }}
                      >
                        {truncateText(song?.title, 23)}
                      </p>
                    )}
                    <p
                      id="hancker"
                      onClick={() => {
                        song?.clickHandle(
                          song?.previewUrl,
                          song?.id,
                          song?.image,
                          song?.title,
                          song?.artist,
                          song?.externalUrl
                        );
                      }}
                    >
                      {' '}
                      {truncateText(song?.artist, 18)}
                    </p>
                  </>
                </div>
              </div>
            ) : (
              <div className="imageName">
                <span className="imgSpan">{song?.image}</span>
                <div className="namePlaylist">{song?.name}</div>
              </div>
              )}
            </div>
          </td>

          {/* Popularity Column */}
          <td
            style={{ textAlign: 'center', position: 'relative' }}
            onClick={() => {
              if (song?.clickHandle) {
                song?.clickHandle(
                  song?.previewUrl,
                  song?.id,
                  song?.image,
                  song?.title,
                  song?.artist,
                  song?.externalUrl
                );
              }
            }}
          >
            <PlanToast
              available={['pro', 'unlimited']}
              titleHeading="Popularity"
            >
              {song?.popularity ?? '--'}
            </PlanToast>
          </td>

          {/* Dates Column */}
          {HideDates ? null : (
            <td style={{ width: '240px' }}>
              <table className="startEndDate">
                <tbody>
                  <tr>
                    <td>
                      <p>Start date:</p>
                    </td>
                    <td>
                      <h3 className="startEndTime">{song?.startTime}</h3>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <p>End date:</p>
                    </td>
                    <td>
                      <h3 className="startEndTime">
                        {song?.endTime ? song?.endTime : '-'}
                      </h3>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          )}

          {/* Spotify Column */}
          <td>
            <a
              className="playSpot"
              target="blank"
              href={'https://open.spotify.com/playlist/' + SearchId}
            >
              <img alt="spotifylogo" src={'./../static/images/spotifyLogo.png'} />
              <p style={{ cursor: 'pointer' }}>Play on Spotify</p>
            </a>
          </td>

          {/* Actions Column */}
          <td className="actionsColumn">
            {!hideMenu && menu ? (
              <svg
                cursor={'pointer'}
                onClick={() => {
                  if (onMenuOpen) {
                    onMenuOpen();
                  }
                  menu(song);
                }}
                stroke="currentColor"
                fill="none"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="26px"
                width="26px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2 5H14V7H2V5Z" fill="currentColor"></path>
                <path d="M2 9H14V11H2V9Z" fill="currentColor"></path>
                <path d="M10 13H2V15H10V13Z" fill="currentColor"></path>
                <path
                  d="M16 9H18V13H22V15H18V19H16V15H12V13H16V9Z"
                  fill="currentColor"
                ></path>
              </svg>
            ) : song?.Edit ? (
              <>{song?.Edit({ ...song, latestindex: index }) || null}</>
            ) : (
              <></>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
});

VirtualizedSongRow.displayName = 'VirtualizedSongRow';

export default VirtualizedSongRow;